class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const previousProgress = this.getFreeShippingProgress();
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.updateFreeShippingProgress(parsedState, previousProgress);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  getFreeShippingProgress() {
    const fillElement = document.querySelector('#CartDrawer-FreeShipping [data-free-shipping-fill]');
    if (!fillElement) return null;

    const currentWidth = Number.parseFloat(fillElement.style.width);
    if (Number.isNaN(currentWidth)) return null;

    return currentWidth;
  }

  updateFreeShippingProgress(cartData, previousProgress) {
    const freeShippingElement = document.getElementById('CartDrawer-FreeShipping');
    if (!freeShippingElement || !cartData) return;
    if (cartData.total_price == null) return;

    const thresholdCents = Number(freeShippingElement.dataset.freeShippingThreshold);
    if (!thresholdCents) return;
    const mode = freeShippingElement.dataset.freeShippingMode || 'auto';
    const bottlePriceCents = Number(freeShippingElement.dataset.bottlePriceCents);

    const cartTotalCents = Number(cartData.total_price) || 0;
    const remainingCents = Math.max(thresholdCents - cartTotalCents, 0);
    const progressPercent = Math.min(Math.max((cartTotalCents / thresholdCents) * 100, 0), 100);
    const messageElement = freeShippingElement.querySelector('[data-free-shipping-message]');
    const fillElement = freeShippingElement.querySelector('[data-free-shipping-fill]');
    const progressbarElement = freeShippingElement.querySelector('[data-free-shipping-progressbar]');
    const remainingTemplate = freeShippingElement.dataset.remainingTemplate || '';
    const remainingBottleTemplate = freeShippingElement.dataset.remainingBottleTemplate || '';
    const remainingBottlesTemplate = freeShippingElement.dataset.remainingBottlesTemplate || '';
    const eligibleMessage = freeShippingElement.dataset.eligibleMessage || '';
    const hasDiscount = this.hasFreeShippingDiscount(cartData);
    const pricingStatus = this.getFreeShippingPricingStatus(cartData, bottlePriceCents);
    const hasRemainder = bottlePriceCents > 0 && remainingCents % bottlePriceCents !== 0;
    const shouldUseCurrency =
      mode === 'currency' ||
      (mode !== 'bottle' &&
        (hasDiscount || pricingStatus.hasMixedBottlePricing || pricingStatus.hasUnknownPricing || hasRemainder));

    if (messageElement) {
      if (remainingCents <= 0) {
        messageElement.innerHTML = eligibleMessage;
      } else if (shouldUseCurrency || bottlePriceCents <= 0) {
        messageElement.innerHTML = remainingTemplate.replace(
          '[amount]',
          this.formatFreeShippingMoney(remainingCents, freeShippingElement.dataset.currencyCode)
        );
      } else {
        const bottlesNeeded = Math.ceil(remainingCents / bottlePriceCents);
        const bottleTemplate = bottlesNeeded === 1 ? remainingBottleTemplate : remainingBottlesTemplate;
        messageElement.innerHTML = bottleTemplate.replace('[count]', String(bottlesNeeded));
      }
    }

    if (progressbarElement) {
      progressbarElement.setAttribute('aria-valuenow', String(Math.round(progressPercent)));
    }

    if (fillElement) {
      if (typeof previousProgress === 'number') {
        fillElement.style.width = `${previousProgress}%`;
      }
      window.requestAnimationFrame(() => {
        fillElement.style.width = `${progressPercent}%`;
      });
    }
  }

  hasFreeShippingDiscount(cartData) {
    const hasCartDiscount = Array.isArray(cartData.cart_level_discount_applications)
      ? cartData.cart_level_discount_applications.length > 0
      : false;
    const hasLineDiscount = Array.isArray(cartData.items)
      ? cartData.items.some(
          (item) =>
            Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.length > 0
        )
      : false;

    return hasCartDiscount || hasLineDiscount;
  }

  getFreeShippingPricingStatus(cartData, bottlePriceCents) {
    if (!Array.isArray(cartData.items) || bottlePriceCents <= 0) {
      return { hasMixedBottlePricing: true, hasUnknownPricing: true };
    }

    let hasMixedBottlePricing = false;
    let hasUnknownPricing = false;

    for (const item of cartData.items) {
      const unitPrice = this.getFreeShippingUnitPrice(item);
      if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
        hasUnknownPricing = true;
        continue;
      }
      if (unitPrice !== bottlePriceCents) {
        hasMixedBottlePricing = true;
      }
    }

    return { hasMixedBottlePricing, hasUnknownPricing };
  }

  getFreeShippingUnitPrice(item) {
    const priceCandidates = [item?.final_price, item?.price, item?.original_price];
    for (const candidate of priceCandidates) {
      const numericCandidate = Number(candidate);
      if (Number.isFinite(numericCandidate) && numericCandidate > 0) {
        return numericCandidate;
      }
    }

    return Number.NaN;
  }

  formatFreeShippingMoney(cents, currencyCode) {
    const locale = document.documentElement.lang || 'en-ZA';
    const activeCurrencyCode = currencyCode || 'ZAR';

    try {
      const formattedValue = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: activeCurrencyCode,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(cents / 100);

      if (activeCurrencyCode === 'ZAR') {
        return formattedValue.replace('ZAR', 'R');
      }

      return formattedValue;
    } catch (error) {
      return `${(cents / 100).toFixed(2)}`;
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
